import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorTemplateSideBarComponent } from '@shared/component/sidebar/error-template-side-bar.component';
import { TierTagComponent } from '@shared/component/tier-tag/tier-tag.component';
import { LOCAL_STORAGE_CONSTANT } from '@shared/constants/local-storage-constant';
import { Pagination } from '@shared/models/pagination.model';
import { CurrentStore } from '@shared/models/store.model';
import { AuthGuardService } from '@shared/services/auth-guard.service';
import { AuthService } from '@shared/services/auth.service';
import { ManagedStoreService } from '@shared/services/managed-store.service';
import { Action, SidebarRefService } from '@shared/services/sidebar-ref.service';
import { UnleashProxyService } from '@shared/services/unleash-proxy.service';
import { LogInSuccessAction, RefreshAction } from '@shared/store/actions/auth.actions';
import { managedStoreAction } from '@shared/store/actions/managed-store.actions';
import { selectAuthoritiesResult, selectUserInfoResult } from '@shared/store/selectors/auth-user-info.selector';
import { selectManagedStoreList } from '@shared/store/selectors/managed-store.selectors';
import { AppStates } from '@shared/store/state/app.state';
import { CookiesUtil } from '@shared/utils/cookies-util';
import { StoreStorageUtil } from '@shared/utils/store-storage-util';
import { CookieService } from 'ngx-cookie-service';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { Observable, Subject, debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs';

import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';
import { environment as env } from '../../environments/environment';
import { APP_ROUTES } from '../app-routing.module';
import { StoreSyncService } from '../services/store-sync.service';

const STORE_LIST_PAGE_SIZE = 1000;
const SEARCH_DEBOUNCE_MS = 500;

@Component({
  selector: 'app-topbar',
  styleUrls: ['./app.topbar.component.scss'],
  templateUrl: './app.topbar.component.html',
  imports: [
    StyleClassModule,
    RippleModule,
    CommonModule,
    TranslateModule,
    TierTagComponent,
    DrawerModule,
    ButtonModule,
    ReactiveFormsModule,
    InputTextModule
  ]
})
export class AppTopbarComponent implements OnInit, OnDestroy {
  @ViewChild('menuButton') menuButton!: ElementRef;

  @ViewChild('mobileMenuButton') mobileMenuButton!: ElementRef;

  @ViewChild('searchInput') searchInput!: ElementRef;

  userInfo$ = new Observable();
  managedStoreList$ = new Observable<Pagination<CurrentStore>>();

  currentStore: CurrentStore;
  currentStore$ = new Observable<CurrentStore>();
  managedStoreList: any[] = [];
  filteredStoreList: any[] = [];
  sideBarRefService = inject(SidebarRefService);
  translate = inject(TranslateService);
  unleashProxyService = inject(UnleashProxyService);
  storeSidebarVisible: boolean = false;
  isSelectedStore: boolean = false;
  selectedStore: any = null;
  searchForm: FormGroup;

  private onComponentDestroy$ = new Subject<void>();

  private cookiesUtil: CookiesUtil;
  private authService: AuthService;

  constructor(
    public layoutService: LayoutService,
    public el: ElementRef,
    private menuService: MenuService,
    protected readonly store: Store<AppStates>,
    private managedStoreService: ManagedStoreService,
    private storeSyncService: StoreSyncService,
    private fb: FormBuilder,
    private authGuardService: AuthGuardService,
    private cookieService: CookieService,
    authService: AuthService,
    private router: Router
  ) {
    this.cookiesUtil = new CookiesUtil(cookieService);
    this.authService = authService;
    this.searchForm = this.fb.group({
      searchCriteria: ['']
    });

    // Subscribe to search input changes
    this.searchForm
      .get('searchCriteria')
      ?.valueChanges.pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.onComponentDestroy$)
      )
      .subscribe((searchTerm) => {
        this.searchStores(searchTerm);
      });
  }

  activeItem!: number;

  get mobileTopbarActive(): boolean {
    return this.layoutService.state.topbarMenuActive;
  }

  ngOnInit(): void {
    this.userInfo$ = this.store.select(selectUserInfoResult).pipe(
      filter((val) => !!val),
      takeUntil(this.onComponentDestroy$)
    );

    this.currentStore = this.retrieveStore();

    if (!this.retrieveStore()?.storeCode || !this.retrieveStore()?.receiptBranchName) {
      this.store.dispatch(managedStoreAction.managedStoreRequest({ page: 1, size: STORE_LIST_PAGE_SIZE }));
    }

    this.managedStoreList$ = this.store
      .select(selectManagedStoreList)
      .pipe(takeUntil(this.onComponentDestroy$))
      .pipe(filter((managedStores) => managedStores?.content !== undefined));
    this.currentStore$ = this.managedStoreList$.pipe(
      map((stores: any) => {
        if (stores) {
          const storeNo = StoreStorageUtil.retrieveStore().storeNo;

          if (stores?.content?.length) {
            const currentStore = stores?.content?.filter((store) => store.storeNo === storeNo)[0];
            if (currentStore) {
              localStorage.setItem(
                LOCAL_STORAGE_CONSTANT.CURRENT_STORE,
                JSON.stringify({
                  storeNo: currentStore?.storeNo,
                  storeCode: currentStore?.storeCode,
                  name: currentStore?.name,
                  receiptBranchName: currentStore?.receiptBranchName
                })
              );

              // ตอน login เก็บมาแค่ storeNo จุดนี้จึงเป็นที่แรกที่รู้ storeCode
              this.unleashProxyService.updateStoreContext(currentStore?.storeCode);
            }
          }
        }

        return this.retrieveStore();
      })
    );

    this.store.dispatch(managedStoreAction.managedStoreRequest({ page: 1, size: STORE_LIST_PAGE_SIZE }));
    this.validateCurrentStoreAccess();
  }

  ngOnDestroy(): void {
    this.onComponentDestroy$.next();
    this.onComponentDestroy$.complete();
    this.storeSyncService.destroy();
  }

  changeStore() {
    this.hideSideMenu();
    const refreshTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.refreshToken);
    const refreshToken = this.cookiesUtil.concatCookie(refreshTokenCookiesName);

    if (refreshToken) {
      this.store.dispatch(new RefreshAction(refreshToken));
      this.store
        .select(selectAuthoritiesResult)
        .pipe(
          filter((authorities) => !!authorities),
          take(1)
        )
        .subscribe(() => {
          this.fetchAndOpenStoreSelector();
        });
    } else {
      this.fetchAndOpenStoreSelector();
    }
  }

  private fetchAndOpenStoreSelector() {
    this.managedStoreList = [];
    this.filteredStoreList = [];

    this.managedStoreService
      .userManagedStoreList({ pageNumber: 0, pageSize: STORE_LIST_PAGE_SIZE })
      .subscribe((response) => {
        if (response?.content) {
          this.managedStoreList = response.content;
          this.filteredStoreList = response.content;
          this.openStoreSelector();
        }
      });
  }

  openStoreSelector() {
    if (this.managedStoreList.length === 1) {
      this.errorCannotChangeStore();
    } else {
      this.storeSidebarVisible = true;
    }
  }

  errorCannotChangeStore() {
    this.onHideStoreSidebar();
    this.sideBarRefService
      .open(ErrorTemplateSideBarComponent, {
        title: 'ทำรายการไม่สำเร็จ',
        okText: 'ตกลง',
        contentMessage: 'ไม่สามารถเปลี่ยนร้านค้าได้',
        hideCancelButton: true
      })
      .subscribe({
        next: (res) => {
          if (res === Action.CONFIRM) {
            this.sideBarRefService.close();
          }
        }
      });
  }

  retrieveStore() {
    return StoreStorageUtil.retrieveStore();
  }

  onMenuButtonClick() {
    if (this.layoutService.isMobile()) {
      this.layoutService.onMenuToggle();
    }
    // else {
    this.layoutService.config.update((config) => ({
      ...config,
      menuMode: config.menuMode === 'static' ? 'overlay' : 'static'
    }));
    // }
  }

  onRightMenuButtonClick() {
    this.layoutService.openRightSidebar();
  }

  onMobileTopbarMenuButtonClick() {
    console.log('should not click');
    this.layoutService.onTopbarMenuToggle();
  }

  focusSearchInput() {
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
    }, 0);
  }

  logout() {
    this.menuService.onLogout();
  }

  hideSideMenu() {
    if (this.layoutService.state.staticMenuMobileActive) {
      this.onMenuButtonClick();
    }
  }

  onHideStoreSidebar() {
    this.storeSidebarVisible = false;
    this.selectedStore = null;
    this.isSelectedStore = false;
    this.clearSearchText();
  }

  onClickSubmitSelectStore() {
    if (this.selectedStore) {
      const storeData = {
        storeNo: this.selectedStore.storeNo,
        storeCode: this.selectedStore.storeCode,
        name: this.selectedStore.name,
        receiptBranchName: this.selectedStore?.receiptBranchName
      };

      const refreshTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.refreshToken);
      const refreshToken = this.cookiesUtil.concatCookie(refreshTokenCookiesName);

      if (refreshToken) {
        this.authService.refreshToken(refreshToken).subscribe({
          next: (credential) => {
            this.store.dispatch(new LogInSuccessAction(credential));

            this.store
              .select(selectAuthoritiesResult)
              .pipe(
                filter((authorities) => !!authorities && authorities.length > 0),
                take(1)
              )
              .subscribe(() => {
                this.validateAndChangeStore(storeData);
              });
          },
          error: () => {
            this.validateAndChangeStore(storeData);
          }
        });
      } else {
        this.validateAndChangeStore(storeData);
      }
    }
  }

  selectStore(store: any) {
    this.selectedStore = store;
    this.isSelectedStore = true;
  }

  clearSearchText() {
    this.searchForm.patchValue({ searchCriteria: '' });
  }

  searchStores(searchTerm: string) {
    const search = searchTerm?.trim() || undefined;

    this.managedStoreService
      .userManagedStoreList({ pageNumber: 0, pageSize: STORE_LIST_PAGE_SIZE, search })
      .subscribe((response) => {
        if (response?.content) {
          this.filteredStoreList = response.content;
        }
      });
  }

  private validateAndChangeStore(storeData: any) {
    this.managedStoreService
      .userManagedStoreList({ pageNumber: 0, pageSize: STORE_LIST_PAGE_SIZE, search: storeData.storeCode })
      .subscribe((response) => {
        if (response?.content) {
          const hasAccess = response.content.some((store) => store.storeNo === storeData.storeNo);

          if (!hasAccess) {
            this.errorCannotChangeStore();
            return;
          }

          if (!this.hasCurrentPagePermission()) {
            this.router.navigate(['/main']);
            return;
          }

          localStorage.setItem(LOCAL_STORAGE_CONSTANT.CURRENT_STORE, JSON.stringify(storeData));
          localStorage.setItem('STORE_JUST_CHANGED', 'true');
          this.storeSyncService.broadcastStoreChange(storeData);

          window.location.reload();
        }
      });
  }

  private hasCurrentPagePermission(): boolean {
    const currentUrl = this.router.url;

    if (currentUrl === '/' || currentUrl === '/main' || currentUrl.startsWith('/main/')) {
      return true;
    }

    const requiredPermissions = this.getPermissionsFromRouting(currentUrl);

    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    return this.authGuardService.checkPermission(requiredPermissions);
  }

  private getPermissionsFromRouting(url: string): string[] {
    const findPermissionsInRoutes = (routes: any[], currentPath: string): string[] => {
      for (const route of routes) {
        if (route.path && currentPath.includes(route.path) && route.data?.permissions) {
          return route.data.permissions;
        }
        if (route.children) {
          const childPermissions = findPermissionsInRoutes(route.children, currentPath);
          if (childPermissions.length > 0) {
            return childPermissions;
          }
        }
      }
      return [];
    };

    return findPermissionsInRoutes(APP_ROUTES, url);
  }

  validateCurrentStoreAccess() {
    const storeJustChanged = localStorage.getItem('STORE_JUST_CHANGED');

    if (storeJustChanged === 'true') {
      localStorage.removeItem('STORE_JUST_CHANGED');

      const currentStore = this.retrieveStore();
      if (!currentStore?.storeNo) {
        return;
      }

      const search = currentStore.storeCode || String(currentStore.storeNo);
      this.managedStoreService
        .userManagedStoreList({ pageNumber: 0, pageSize: STORE_LIST_PAGE_SIZE, search })
        .subscribe((response) => {
          if (response?.content) {
            const hasAccess = response.content.some((store) => store.storeNo === currentStore.storeNo);
            if (!hasAccess) {
              this.errorCannotChangeStore();
            }
          }
        });
    }
  }
}
